import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type NavigateOptions = {
  replace?: boolean;
};

type RouterContextValue = {
  path: string;
  navigate: (to: string, options?: NavigateOptions) => void;
};

const RouterContext = createContext<RouterContextValue | undefined>(undefined);

const normalizePath = (path: string) => {
  if (!path) return '/';
  return path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
};

export const RouterProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [path, setPath] = useState(() => normalizePath(window.location.pathname));

  useEffect(() => {
    const handlePopstate = () => {
      setPath(normalizePath(window.location.pathname));
    };
    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, []);

  const value = useMemo<RouterContextValue>(() => ({
    path,
    navigate: (to: string, options?: NavigateOptions) => {
      // Strip query string — path state tracks pathname only
      const next = normalizePath(to.split('?')[0]);
      if (options?.replace) {
        window.history.replaceState({}, '', to);
      } else {
        window.history.pushState({}, '', to);
      }
      if (next !== path) setPath(next);
    },
  }), [path]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
};

export const useRouter = () => {
  const context = useContext(RouterContext);
  if (!context) {
    throw new Error('useRouter must be used within a RouterProvider');
  }
  return context;
};
